import classNames from "classnames";
import { useFeature } from "flagged";
import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";

import { deduceTierLicenses } from "./add-tenant/AddTenantForm";
import { CHECKMARK_SIZE, STROKE_WIDTH, updateSelectedSmbTierTitle, updateSelectedTierTitle } from "./PricingTierView";
import Checkmark from "components/icons/Checkmark";
import {
    BundlesTenantTypes,
    CHANNEL_BUNDLES,
    ChannelBundle,
    CIRCULATE_BUNDLES,
    CirculateBundle,
    CorporateBundle,
    MOBILE_PROCESSOR_BUNDLES,
    MOBILE_RETAIL_BUNDLES,
    MobileProcessorBundle,
    MobileRetailBundle,
    PROCESSOR_BUNDLES,
    ProcessorBundle,
    SmbBundle,
} from "components/licenses/bundles";
import { createProductIdToNameMap } from "components/licenses/common";
import StaticTable from "components/support/api-guide/StaticTable";
import style from "components/tenants/pricing-tier-view.scss";
import { CombinedTier, TenantType } from "domain/tenants";
import { generateTenantTypeToTranslationMap, generateTierToTranslationMap } from "domain/tierRelatedMaps";
import { FLAG_FILE_SHARING } from "services/feature/FeatureFlagService";
import { StoreState } from "store";
import { resolveColor } from "utils/commonFunctions";

import testIds from "testIds.json";

interface Props {
    tenantCreation?: boolean;
    tenantType: TenantType;
    selectedTier: CombinedTier;
    currentProductsInRateCard?: string[];
}

enum BmpFeatures {
    LICENSE_ALERTS = "License alerts",
    LICENSE_VIEW_AND_ENTITLEMENTS = "Blancco license view and entitlements",
    LICENSE_POOLS = "License pools",
    LICENSE_CONSUMPTION = "License consumption",
    PRODUCT_DOWNLOADS = "Product downloads",
    KNOWLEDGEBASE = "Knowledge base",
    TECHNICAL_SUPPORT_SP1 = "Technical Support SP1",
    TECHNICAL_SUPPORT_SP2 = "Technical Support SP2",
    TECHNICAL_SUPPORT_SPE = "Technical Support SPE",
    KEY_VALUE_STORE = "Key value store",
    BMP_APIS = "BMP APIs",
    CENTRALIZED_REPORTING = "Centralized reporting",
    REPORTING_CUSTOM_VIEWS = "Reporting custom views",
    SUSTAINABILITY_DASHBOARD = "Your impact on sustainability",
    WORKFLOWS = "Intelligent Business Routing (IBR / Workflows)",
    REPORT_CUSTOMIZATION = "Report template library",
    OVERVIEW_DASHBOARD = "Overview dashboard",
    SSO = "Single sign-on (SSO)",
    TENANT_MANAGEMENT = "Tenant management",
    USER_GROUPS = "User groups",
    CUSTOM_ROLES = "Custom roles",
    BMC = "On-premise BMP",
    FILE_SHARING = "File sharing",
}

interface TierFeature {
    name: string;
    corporateBundles: CorporateBundle[];
    processorBundles: ProcessorBundle[];
    smbBundles: SmbBundle[];
    channelBundles: ChannelBundle[];
    mobileRetailBundles: MobileRetailBundle[];
    mobileProcessorBundles: MobileProcessorBundle[];
    circulateBundles: CirculateBundle[];
}

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

const NewModelPricingTierView = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const isFileSharingFlagActive = useFeature(FLAG_FILE_SHARING);
    const allMobileRetailBundles = [
        MobileRetailBundle.MOBILE_RETAIL_LITE,
        MobileRetailBundle.MOBILE_RETAIL_PLUS,
        MobileRetailBundle.MOBILE_RETAIL_PRO,
    ];
    const allMobileProcessorBundles = [
        MobileProcessorBundle.MOBILE_PROCESSOR_CORE,
        MobileProcessorBundle.MOBILE_PROCESSOR_PRO,
        MobileProcessorBundle.MOBILE_PROCESSOR_PRO_SCALE,
    ];

    const allCorporateBundles = [
        CorporateBundle.STARTER,
        CorporateBundle.ESSENTIALS,
        CorporateBundle.POWER,
        CorporateBundle.ENTERPRISE,
    ];
    const allProcessorBundles = [ProcessorBundle.CORE, ProcessorBundle.PRO, ProcessorBundle.PRO_SCALE];
    const allSmbBundles = [
        SmbBundle.SELECT_50 || SmbBundle.SELECT_100,
        SmbBundle.SELECT_PLUS_250 || SmbBundle.SELECT_PLUS_500,
    ];

    const allChannelBundles = [ChannelBundle.PRO_SCALE];
    const allCirculateBundles = [CirculateBundle.STANDALONE];
    const deducedTierLicenses = deduceTierLicenses(props.selectedTier, true, true, props.currentProductsInRateCard);
    const availableLicensesFeatures: TierFeature[] = [
        {
            name: BmpFeatures.LICENSE_VIEW_AND_ENTITLEMENTS,
            corporateBundles: allCorporateBundles,
            processorBundles: allProcessorBundles,
            smbBundles: allSmbBundles,
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: allMobileProcessorBundles,
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.LICENSE_ALERTS,
            corporateBundles: allCorporateBundles,
            processorBundles: allProcessorBundles,
            smbBundles: allSmbBundles,
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: allMobileProcessorBundles,
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.LICENSE_POOLS,
            corporateBundles: allCorporateBundles,
            processorBundles: allProcessorBundles,
            smbBundles: allSmbBundles,
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: allMobileProcessorBundles,
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.PRODUCT_DOWNLOADS,
            corporateBundles: allCorporateBundles,
            processorBundles: allProcessorBundles,
            smbBundles: allSmbBundles,
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: allMobileProcessorBundles,
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.KNOWLEDGEBASE,
            corporateBundles: allCorporateBundles,
            processorBundles: allProcessorBundles,
            smbBundles: allSmbBundles,
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: allMobileProcessorBundles,
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.CUSTOM_ROLES,
            corporateBundles: allCorporateBundles,
            processorBundles: allProcessorBundles,
            smbBundles: allSmbBundles,
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: allMobileProcessorBundles,
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.KEY_VALUE_STORE,
            corporateBundles: allCorporateBundles,
            processorBundles: allProcessorBundles,
            smbBundles: allSmbBundles,
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: allMobileProcessorBundles,
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.SSO,
            corporateBundles: allCorporateBundles,
            processorBundles: allProcessorBundles,
            smbBundles: allSmbBundles,
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: [
                MobileProcessorBundle.MOBILE_PROCESSOR_PRO,
                MobileProcessorBundle.MOBILE_PROCESSOR_PRO_SCALE,
            ],
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.SUSTAINABILITY_DASHBOARD,
            corporateBundles: [CorporateBundle.POWER, CorporateBundle.ENTERPRISE],
            processorBundles: [ProcessorBundle.PRO, ProcessorBundle.PRO_SCALE],
            smbBundles: [],
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: [
                MobileProcessorBundle.MOBILE_PROCESSOR_PRO,
                MobileProcessorBundle.MOBILE_PROCESSOR_PRO_SCALE,
            ],
            circulateBundles: [],
        },
        {
            name: BmpFeatures.WORKFLOWS,
            corporateBundles: [CorporateBundle.POWER, CorporateBundle.ENTERPRISE],
            processorBundles: [ProcessorBundle.PRO, ProcessorBundle.PRO_SCALE],
            smbBundles: [],
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: [
                MobileProcessorBundle.MOBILE_PROCESSOR_PRO,
                MobileProcessorBundle.MOBILE_PROCESSOR_PRO_SCALE,
            ],
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.USER_GROUPS,
            corporateBundles: [CorporateBundle.POWER, CorporateBundle.ENTERPRISE],
            processorBundles: [ProcessorBundle.PRO, ProcessorBundle.PRO_SCALE],
            smbBundles: [],
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: [
                MobileProcessorBundle.MOBILE_PROCESSOR_PRO,
                MobileProcessorBundle.MOBILE_PROCESSOR_PRO_SCALE,
            ],
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.OVERVIEW_DASHBOARD,
            corporateBundles: [CorporateBundle.ENTERPRISE],
            processorBundles: [ProcessorBundle.PRO, ProcessorBundle.PRO_SCALE],
            smbBundles: [],
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: allMobileProcessorBundles,
            circulateBundles: [],
        },
        {
            name: BmpFeatures.REPORT_CUSTOMIZATION,
            corporateBundles: [CorporateBundle.ENTERPRISE],
            processorBundles: [ProcessorBundle.PRO_SCALE],
            smbBundles: [],
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: [MobileProcessorBundle.MOBILE_PROCESSOR_PRO_SCALE],
            circulateBundles: [],
        },
        {
            name: BmpFeatures.TENANT_MANAGEMENT,
            corporateBundles: [CorporateBundle.ENTERPRISE],
            processorBundles: [ProcessorBundle.PRO_SCALE],
            smbBundles: [],
            channelBundles: allChannelBundles,
            mobileRetailBundles: [],
            mobileProcessorBundles: [MobileProcessorBundle.MOBILE_PROCESSOR_PRO_SCALE],
            circulateBundles: allCirculateBundles,
        },
    ];
    const availableMobileRetailFeatures: TierFeature[] = [
        {
            name: BmpFeatures.LICENSE_CONSUMPTION,
            corporateBundles: allCorporateBundles,
            processorBundles: allProcessorBundles,
            smbBundles: allSmbBundles,
            channelBundles: allChannelBundles,
            mobileRetailBundles: allMobileRetailBundles,
            mobileProcessorBundles: allMobileProcessorBundles,
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.CENTRALIZED_REPORTING,
            corporateBundles: allCorporateBundles,
            processorBundles: allProcessorBundles,
            smbBundles: allSmbBundles,
            channelBundles: allChannelBundles,
            mobileRetailBundles: allMobileRetailBundles,
            mobileProcessorBundles: allMobileProcessorBundles,
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.REPORTING_CUSTOM_VIEWS,
            corporateBundles: allCorporateBundles,
            processorBundles: allProcessorBundles,
            smbBundles: allSmbBundles,
            channelBundles: allChannelBundles,
            mobileRetailBundles: allMobileRetailBundles,
            mobileProcessorBundles: allMobileProcessorBundles,
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.BMP_APIS,
            corporateBundles: [CorporateBundle.POWER, CorporateBundle.ENTERPRISE],
            processorBundles: [ProcessorBundle.PRO, ProcessorBundle.PRO_SCALE],
            smbBundles: [],
            channelBundles: allChannelBundles,
            mobileRetailBundles: allMobileRetailBundles,
            mobileProcessorBundles: [
                MobileProcessorBundle.MOBILE_PROCESSOR_PRO,
                MobileProcessorBundle.MOBILE_PROCESSOR_PRO_SCALE,
            ],
            circulateBundles: allCirculateBundles,
        },
        {
            name: BmpFeatures.TECHNICAL_SUPPORT_SP2,
            corporateBundles: [CorporateBundle.ESSENTIALS],
            processorBundles: [ProcessorBundle.CORE],
            smbBundles: [SmbBundle.SELECT_PLUS_250, SmbBundle.SELECT_PLUS_500],
            channelBundles: [],
            mobileRetailBundles: [MobileRetailBundle.MOBILE_RETAIL_LITE],
            mobileProcessorBundles: [MobileProcessorBundle.MOBILE_PROCESSOR_CORE],
            circulateBundles: [],
        },
        {
            name: BmpFeatures.TECHNICAL_SUPPORT_SP1,
            corporateBundles: [CorporateBundle.POWER],
            processorBundles: [ProcessorBundle.PRO],
            smbBundles: [],
            channelBundles: [],
            mobileRetailBundles: [MobileRetailBundle.MOBILE_RETAIL_PLUS],
            mobileProcessorBundles: [MobileProcessorBundle.MOBILE_PROCESSOR_PRO],
            circulateBundles: [],
        },
        {
            name: BmpFeatures.TECHNICAL_SUPPORT_SPE,
            corporateBundles: [CorporateBundle.ENTERPRISE],
            processorBundles: [ProcessorBundle.PRO_SCALE],
            smbBundles: [],
            channelBundles: allChannelBundles,
            mobileRetailBundles: [MobileRetailBundle.MOBILE_RETAIL_PRO],
            mobileProcessorBundles: [MobileProcessorBundle.MOBILE_PROCESSOR_PRO_SCALE],
            circulateBundles: allCirculateBundles,
        },
    ];
    isFileSharingFlagActive &&
        availableMobileRetailFeatures.push({
            name: BmpFeatures.FILE_SHARING,
            corporateBundles: allCorporateBundles,
            processorBundles: allProcessorBundles,
            smbBundles: allSmbBundles,
            channelBundles: allChannelBundles,
            mobileRetailBundles: allMobileRetailBundles,
            mobileProcessorBundles: allMobileProcessorBundles,
            circulateBundles: allCirculateBundles,
        });

    let availableFeatures: TierFeature[];

    if (props.tenantType == BundlesTenantTypes.MOBILE_RETAIL) {
        availableFeatures = [...availableMobileRetailFeatures];
    } else if (props.tenantType == BundlesTenantTypes.CIRCULATE) {
        availableFeatures = [...availableLicensesFeatures];
    } else {
        availableFeatures = [...availableLicensesFeatures, ...availableMobileRetailFeatures];
    }

    function deduceTierTranslationKey(
        tierType: string,
        selected: boolean
    ):
        | {
              translationKey: string;
          }
        | undefined {
        const corporateBundlesToTranslationKey = new Map<string, { translationKey: string }>([
            [
                CorporateBundle.STARTER.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(selected, CorporateBundle.STARTER, props.selectedTier, t),
                },
            ],
            [
                CorporateBundle.ESSENTIALS.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(
                        selected,
                        CorporateBundle.ESSENTIALS,
                        props.selectedTier,
                        t
                    ),
                },
            ],
            [
                CorporateBundle.POWER.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(selected, CorporateBundle.POWER, props.selectedTier, t),
                },
            ],
            [
                CorporateBundle.ENTERPRISE.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(
                        selected,
                        CorporateBundle.ENTERPRISE,
                        props.selectedTier,
                        t
                    ),
                },
            ],
        ]);

        const processorBundlesToTranslationKey = new Map<string, { translationKey: string }>([
            [
                ProcessorBundle.CORE.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(selected, ProcessorBundle.CORE, props.selectedTier, t),
                },
            ],
            [
                ProcessorBundle.PRO.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(selected, ProcessorBundle.PRO, props.selectedTier, t),
                },
            ],
            [
                ProcessorBundle.PRO_SCALE.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(selected, ProcessorBundle.PRO_SCALE, props.selectedTier, t),
                },
            ],
        ]);
        const smbBundlesToTranslationKey = new Map<string, { translationKey: string }>([
            [
                SmbBundle.SELECT_50.valueOf(),
                {
                    translationKey: updateSelectedSmbTierTitle(selected, SmbBundle.SELECT_50, props.selectedTier, t),
                },
            ],
            [
                SmbBundle.SELECT_100.valueOf(),
                {
                    translationKey: updateSelectedSmbTierTitle(selected, SmbBundle.SELECT_100, props.selectedTier, t),
                },
            ],
            [
                SmbBundle.SELECT_PLUS_250.valueOf(),
                {
                    translationKey: updateSelectedSmbTierTitle(
                        selected,
                        SmbBundle.SELECT_PLUS_250,
                        props.selectedTier,
                        t
                    ),
                },
            ],
            [
                SmbBundle.SELECT_PLUS_500.valueOf(),
                {
                    translationKey: updateSelectedSmbTierTitle(
                        selected,
                        SmbBundle.SELECT_PLUS_500,
                        props.selectedTier,
                        t
                    ),
                },
            ],
        ]);
        const channelBundlesToTranslationKey = new Map<string, { translationKey: string }>([
            [
                ChannelBundle.PRO_SCALE.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(selected, ChannelBundle.PRO_SCALE, props.selectedTier, t),
                },
            ],
        ]);

        const mobileRetailBundlesToTranslationKey = new Map<string, { translationKey: string }>([
            [
                MobileRetailBundle.MOBILE_RETAIL_LITE.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(
                        selected,
                        MobileRetailBundle.MOBILE_RETAIL_LITE,
                        props.selectedTier,
                        t
                    ),
                },
            ],
            [
                MobileRetailBundle.MOBILE_RETAIL_PLUS.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(
                        selected,
                        MobileRetailBundle.MOBILE_RETAIL_PLUS,
                        props.selectedTier,
                        t
                    ),
                },
            ],
            [
                MobileRetailBundle.MOBILE_RETAIL_PRO.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(
                        selected,
                        MobileRetailBundle.MOBILE_RETAIL_PRO,
                        props.selectedTier,
                        t
                    ),
                },
            ],
        ]);

        const mobileProcessorBundlesToTranslationKey = new Map<string, { translationKey: string }>([
            [
                MobileProcessorBundle.MOBILE_PROCESSOR_CORE.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(
                        selected,
                        MobileProcessorBundle.MOBILE_PROCESSOR_CORE,
                        props.selectedTier,
                        t
                    ),
                },
            ],
            [
                MobileProcessorBundle.MOBILE_PROCESSOR_PRO.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(
                        selected,
                        MobileProcessorBundle.MOBILE_PROCESSOR_PRO,
                        props.selectedTier,
                        t
                    ),
                },
            ],
            [
                MobileProcessorBundle.MOBILE_PROCESSOR_PRO_SCALE.valueOf(),
                {
                    translationKey: updateSelectedTierTitle(
                        selected,
                        MobileProcessorBundle.MOBILE_PROCESSOR_PRO_SCALE,
                        props.selectedTier,
                        t
                    ),
                },
            ],
        ]);

        if (props.tenantType == BundlesTenantTypes.CORPORATE) {
            return corporateBundlesToTranslationKey.get(tierType);
        } else if (props.tenantType == BundlesTenantTypes.PROCESSOR) {
            return processorBundlesToTranslationKey.get(tierType);
        } else if (props.tenantType == BundlesTenantTypes.SMB) {
            return smbBundlesToTranslationKey.get(tierType);
        } else if (props.tenantType == BundlesTenantTypes.CHANNEL) {
            return channelBundlesToTranslationKey.get(tierType);
        } else if (props.tenantType == BundlesTenantTypes.MOBILE_RETAIL) {
            return mobileRetailBundlesToTranslationKey.get(tierType);
        } else if (props.tenantType == BundlesTenantTypes.MOBILE_PROCESSOR) {
            return mobileProcessorBundlesToTranslationKey.get(tierType);
        } else if (props.tenantType == BundlesTenantTypes.CIRCULATE) {
            return {
                translationKey: updateSelectedTierTitle(selected, CirculateBundle.STANDALONE, props.selectedTier, t),
            };
        }
    }

    function computeTier(): string[] {
        if (props.tenantType == BundlesTenantTypes.PROCESSOR) {
            return PROCESSOR_BUNDLES;
        } else if (props.tenantType == BundlesTenantTypes.SMB) {
            return allSmbBundles.map((bundle) => bundle.valueOf());
        } else if (props.tenantType == BundlesTenantTypes.CHANNEL) {
            return CHANNEL_BUNDLES;
        } else if (props.tenantType == BundlesTenantTypes.MOBILE_RETAIL) {
            return MOBILE_RETAIL_BUNDLES;
        } else if (props.tenantType == BundlesTenantTypes.MOBILE_PROCESSOR) {
            return MOBILE_PROCESSOR_BUNDLES;
        } else if (props.tenantType == BundlesTenantTypes.CIRCULATE) {
            return CIRCULATE_BUNDLES;
        }
        return Object.keys(CorporateBundle);
    }

    function createHeaderForVolumeLicences() {
        const headers = [];
        headers.push({
            className: style.firstColumnHeader,
            value: t("AddCustomerForm.pricingTierDialog.header.licences"),
        });
        return headers;
    }

    function createHeaders() {
        const tiers = computeTier();
        const headers = [];
        headers.push({
            className: style.firstColumnHeader,
            value: t("AddCustomerForm.pricingTierDialog.header.features"),
        });
        tiers.forEach((each) => {
            const tier = deduceTierTranslationKey(each, props.selectedTier === each);
            headers.push({
                value: <div className={style.headerContaner}>{tier?.translationKey}</div>,
            });
        });
        return headers;
    }

    function displayAvailability(
        tier:
            | CorporateBundle
            | ProcessorBundle
            | SmbBundle
            | ChannelBundle
            | MobileRetailBundle
            | MobileProcessorBundle
            | CirculateBundle,
        corporateBundles: CorporateBundle[],
        processorBundles: ProcessorBundle[],
        smbBundles: SmbBundle[],
        channelBundle: ChannelBundle[],
        mobileRetailBundle: MobileRetailBundle[],
        mobileProcessorBundle: MobileProcessorBundle[],
        circulateBundle: CirculateBundle[]
    ) {
        if (props.tenantType == BundlesTenantTypes.SMB) {
            return smbBundles.includes(tier as SmbBundle);
        } else if (props.tenantType == BundlesTenantTypes.CORPORATE) {
            return corporateBundles.includes(tier as CorporateBundle);
        } else if (props.tenantType == BundlesTenantTypes.PROCESSOR) {
            return processorBundles.includes(tier as ProcessorBundle);
        } else if (props.tenantType == BundlesTenantTypes.MOBILE_RETAIL) {
            return mobileRetailBundle.includes(tier as MobileRetailBundle);
        } else if (props.tenantType == BundlesTenantTypes.MOBILE_PROCESSOR) {
            return mobileProcessorBundle.includes(tier as MobileProcessorBundle);
        } else if (props.tenantType == BundlesTenantTypes.CIRCULATE) {
            return circulateBundle.includes(tier as CirculateBundle);
        }
        return channelBundle.includes(tier as ChannelBundle);
    }

    function generateListOfVolumeLicenses() {
        const productsNameList: string[] = [];
        const ALL_PRODUCTS = createProductIdToNameMap();
        deducedTierLicenses?.forEach((each) => {
            productsNameList.push(ALL_PRODUCTS.get(each) as string);
        });

        return productsNameList.sort();
    }

    function getDisplayTierForSelected(selectedTier: CombinedTier): CombinedTier {
        if (selectedTier === SmbBundle.SELECT_100) {
            return SmbBundle.SELECT_50;
        }
        if (selectedTier === SmbBundle.SELECT_PLUS_500) {
            return SmbBundle.SELECT_PLUS_250;
        }
        return selectedTier;
    }

    function generateCellData() {
        let tiers:
            | CorporateBundle[]
            | ProcessorBundle[]
            | SmbBundle[]
            | ChannelBundle[]
            | MobileRetailBundle[]
            | MobileProcessorBundle[]
            | CirculateBundle[];
        if (props.tenantType == BundlesTenantTypes.CORPORATE) {
            tiers = allCorporateBundles;
        } else if (props.tenantType == BundlesTenantTypes.PROCESSOR) {
            tiers = allProcessorBundles;
        } else if (props.tenantType == BundlesTenantTypes.SMB) {
            tiers = allSmbBundles;
        } else if (props.tenantType == BundlesTenantTypes.CHANNEL) {
            tiers = allChannelBundles;
        } else if (props.tenantType == BundlesTenantTypes.MOBILE_RETAIL) {
            tiers = allMobileRetailBundles;
        } else if (props.tenantType == BundlesTenantTypes.MOBILE_PROCESSOR) {
            tiers = allMobileProcessorBundles;
        } else if (props.tenantType == BundlesTenantTypes.CIRCULATE) {
            tiers = allCirculateBundles;
        }
        if (!props.tenantType) {
            availableFeatures.push({
                name: BmpFeatures.BMC,
                corporateBundles: [],
                processorBundles: [ProcessorBundle.PRO_SCALE],
                smbBundles: [],
                channelBundles: [ChannelBundle.PRO_SCALE],
                mobileRetailBundles: [],
                mobileProcessorBundles: [],
                circulateBundles: [],
            });
        }

        const tierIndexToColorMap = new Map<number, { backgroundColor: string; iconColor: string }>([
            [
                0,
                {
                    backgroundColor: style.tierFirstLevelColor,
                    iconColor:
                        props.selectedTier === "STANDALONE"
                            ? resolveColor("--app-chart-chartTenthSet")
                            : style.homeCurrentPlanIconYellowColor,
                },
            ],
            [1, { backgroundColor: style.tierSecondLevelColor, iconColor: style.homeCurrentPlanIconBlueColor }],
            [2, { backgroundColor: style.tierThirdLevelColor, iconColor: style.homeCurrentPlanIconRedColor }],
            [3, { backgroundColor: style.tierThirdLevelColor, iconColor: style.homeCurrentPlanIconRedColor }],
        ]);
        if (props.tenantType == BundlesTenantTypes.SMB) {
            tierIndexToColorMap.set(2, {
                backgroundColor: style.tierFourthLevelColor,
                iconColor: props.theme.diamondTierBannerBorderColor,
            });
        }
        return availableFeatures.map((eachLicense) => {
            return [
                <td key={"row" + "-" + eachLicense.name}>
                    <div key={eachLicense.name}>{eachLicense.name}</div>
                </td>,
                ...tiers.map((eachTier, index) => {
                    const tierColor = tierIndexToColorMap.get(index);
                    const backgroundColor = tierColor?.backgroundColor || "";
                    const iconColor = tierColor?.iconColor || "";
                    return (
                        <td
                            key={eachTier + "-" + eachLicense}
                            className={classNames({
                                [backgroundColor]: getDisplayTierForSelected(props.selectedTier) === eachTier,
                            })}
                        >
                            <div className={style.checkMarkCenter}>
                                {displayAvailability(
                                    eachTier,
                                    eachLicense.corporateBundles,
                                    eachLicense.processorBundles,
                                    eachLicense.smbBundles,
                                    eachLicense.channelBundles,
                                    eachLicense.mobileRetailBundles,
                                    eachLicense.mobileProcessorBundles,
                                    eachLicense.circulateBundles
                                ) ? (
                                    <Checkmark color={iconColor} size={CHECKMARK_SIZE} strokeWidth={STROKE_WIDTH} />
                                ) : null}
                            </div>
                        </td>
                    );
                }),
            ];
        });
    }

    function createFeatureLicensesSummary() {
        return (
            <Trans
                i18nKey="AddCustomerForm.pricingTierDialog.summary2"
                values={{
                    tenantType: generateTenantTypeToTranslationMap(t).get(props.tenantType),
                    tierPlan: generateTierToTranslationMap(t).get(props.selectedTier),
                    tierOrBundle: t("AddCustomerForm.pricingTierDialog.bundle"),
                }}
                components={[<strong key={"tenantType"} />]}
            />
        );
    }

    function createVolumeLicensesSummary() {
        return t("AddCustomerForm.pricingTierDialog.summaryVolLicences", {
            tenantType: generateTenantTypeToTranslationMap(t).get(props.tenantType),
            tierPlan: generateTierToTranslationMap(t).get(props.selectedTier),
            tierOrBundle: t("AddCustomerForm.pricingTierDialog.bundle"),
        });
    }

    return (
        <div>
            <Tabs className={style.tabsSegmentVariant}>
                <TabList>
                    <Tab>{t("AddCustomerForm.pricingTierDialog.header.features")} </Tab>
                    <Tab>{t("AddCustomerForm.pricingTierDialog.header.volumeLicenses")} </Tab>
                </TabList>
                <TabPanel>
                    <div className={style.summaryBundles}>{createFeatureLicensesSummary()}</div>
                    <div className={style.tableContainer}>
                        <StaticTable
                            headers={createHeaders()}
                            styledCells={generateCellData()}
                            tableClass={style.table}
                            testId={testIds.workArea.tenant.pricingTierDialog.table.itself}
                        />
                    </div>
                </TabPanel>
                <TabPanel>
                    <div>
                        <div className={style.summaryBundles}>{createVolumeLicensesSummary()}</div>
                        <div className={style.tableContainer}>
                            <StaticTable
                                headers={createHeaderForVolumeLicences()}
                                cells={generateListOfVolumeLicenses().map((each) => {
                                    return [<div key={each}>{each}</div>];
                                })}
                                tableClass={style.table}
                                testId={testIds.workArea.tenant.pricingTierDialog.table.itself}
                            />
                        </div>
                    </div>
                </TabPanel>
            </Tabs>
        </div>
    );
};

export default connector(NewModelPricingTierView);
